import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ModalShell from '@shared/components/ModalShell';
import Input from '@shared/components/Input';
import StatusBadge from '@/components/StatusBadge';
import { displayStateBadge, providerLabel } from '@/lib/subscriptionStatus';
import { searchGlobal, type SearchResponse } from '@/services/api';
import { Icon, type IconName } from '@shared';

/** Pantallas a las que se puede saltar escribiendo su nombre. */
const PAGES: { label: string; path: string; icon: IconName; keywords?: string }[] = [
  { label: 'Dashboard', path: '/', icon: 'dashboard' },
  { label: 'Monitor · Registro', path: '/monitor?tab=registro', icon: 'history', keywords: 'eventos auditoria actividad log' },
  // Las alarmas con estado; el detalle de una es `/monitor/alarmas/{id}`.
  { label: 'Monitor · Alarmas', path: '/monitor?tab=alarmas', icon: 'notification_important', keywords: 'avisos atencion' },
  { label: 'Monitor · Errores', path: '/monitor?tab=errores', icon: 'bug_report', keywords: 'sentry issues' },
  { label: 'Talleres', path: '/talleres', icon: 'business' },
  { label: 'Migraciones', path: '/migraciones', icon: 'drive_file_move', keywords: 'expedientes migracion importar carpeta revisada ia' },
  { label: 'Usuarios', path: '/usuarios', icon: 'group' },
  { label: 'Usuarios · Invitaciones', path: '/usuarios?tab=invitaciones', icon: 'mail', keywords: 'invitaciones invitar pendientes' },
  { label: 'Soporte', path: '/soporte', icon: 'support_agent' },
  { label: 'Suscripciones', path: '/suscripciones', icon: 'credit_card' },
  { label: 'Planes y límites', path: '/planes', icon: 'sell', keywords: 'limites entitlements' },
  { label: 'Financiero', path: '/financiero', icon: 'account_balance' },
  { label: 'Partners', path: '/partners', icon: 'handshake' },
  { label: 'Estadísticas', path: '/analytics', icon: 'analytics', keywords: 'metricas totales' },
  { label: 'Países', path: '/paises', icon: 'public', keywords: 'impuestos iva facturacion monedas fechas limite registro' },
  { label: 'Catálogo', path: '/catalogo', icon: 'menu_book' },
  { label: 'Email', path: '/email', icon: 'mail', keywords: 'plantillas resend newsletter listmonk listas' },
  { label: 'Traducciones', path: '/traducciones', icon: 'translate' },
  { label: 'Impresoras', path: '/impresoras', icon: 'print', keywords: 'impresoras modelos conectadas agente local agentes flota' },
  { label: 'Versiones del agente', path: '/versiones-agente', icon: 'system_update_alt', keywords: 'canales de version dev prod plugins cascaron publicar promover imagen agente local' },
  { label: 'Procesos', path: '/procesos', icon: 'memory', keywords: 'workers asignaciones tareas programadas recurrentes base de datos conexiones postgres pool' },
  { label: 'Almacenamiento', path: '/almacenamiento', icon: 'cloud' },
  { label: 'Tareas y roadmap', path: '/desarrollo', icon: 'code', keywords: 'desarrollo kanban versiones roadmap' },
  { label: 'Test', path: '/test', icon: 'science' },
  { label: 'Configuración', path: '/configuracion', icon: 'settings', keywords: 'ajustes settings' },
  { label: 'Monitor · Ajustes', path: '/monitor?tab=ajustes', icon: 'tune', keywords: 'ntfy avisos al movil canales reglas push alarmas retencion umbrales detectores reconocer avisos del taller' },
  { label: 'Monitor · Entregas', path: '/monitor?tab=entregas', icon: 'outbox', keywords: 'ntfy avisos enviados entregas omitidos reintentar' },
  { label: 'Mis talleres', path: '/mi-cuenta', icon: 'account_circle', keywords: 'mi cuenta organizaciones' },
];

type Item = { key: string; icon: IconName; title: string; subtitle?: string; path: string; badge?: { status: string; label: string } };

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Búsqueda global (Ctrl/Cmd+K): pantallas por nombre y, a partir de dos
 * caracteres, talleres, usuarios y suscripciones por `GET /api/search`.
 * Vive en el admin (un solo consumidor); se subirá a `@shared` el día que
 * el tenant la pida.
 */
export default function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [remote, setRemote] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => { if (open) { setQ(''); setRemote(null); setActive(0); } }, [open]);

  // Búsqueda remota con rebote, sólo con dos caracteres o más.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setRemote(null); return; }
    let cancelled = false;
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const d = await searchGlobal(term);
        if (!cancelled) setRemote(d);
      } catch {
        if (!cancelled) setRemote(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [q, open]);

  const items = useMemo<Item[]>(() => {
    const term = norm(q.trim());
    const pages = PAGES
      .filter((p) => !term || norm(`${p.label} ${p.keywords ?? ''}`).includes(term))
      .slice(0, term ? 5 : 8)
      .map((p) => ({ key: `page:${p.path}`, icon: p.icon, title: p.label, subtitle: 'Ir a la pantalla', path: p.path }));
    const workshops = (remote?.workshops ?? []).map((w): Item => ({
      key: `w:${w.id}`, icon: 'business', title: w.name, subtitle: `${w.slug}${w.owner_email ? ` · ${w.owner_email}` : ''}${w.plan_name ? ` · ${w.plan_name}` : ''}`,
      path: `/talleres/${w.id}`, badge: w.deleted ? { status: 'deleted', label: 'Borrado' } : undefined,
    }));
    const users = (remote?.users ?? []).map((u): Item => ({
      key: `u:${u.id}`, icon: 'person', title: u.name || u.email || `#${u.id}`, subtitle: u.email ?? undefined, path: `/usuarios/${u.id}`,
      badge: u.is_active ? undefined : { status: 'inactive', label: 'Inactivo' },
    }));
    const subs = (remote?.subscriptions ?? []).map((s): Item => {
      const b = displayStateBadge({ display_state: s.display_state });
      return {
        key: `s:${s.id}`, icon: 'credit_card', title: `${s.workshop_name} · ${s.plan_name ?? 'sin plan'}`,
        subtitle: `Suscripción #${s.id}${s.provider ? ` · ${providerLabel(s.provider)}` : ''}`,
        path: `/talleres/${s.workshop_id}?tab=suscripcion`, badge: { status: b.status, label: b.label },
      };
    });
    return [...workshops, ...users, ...subs, ...pages];
  }, [q, remote]);

  useEffect(() => { setActive(0); }, [items.length, q]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const go = (item: Item) => { onClose(); navigate(item.path); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const it = items[active]; if (it) go(it); }
  };

  return (
    <ModalShell isOpen={open} onClose={onClose} title="Buscar" subtitle="Talleres, usuarios, suscripciones y pantallas · ↑↓ para moverte, Enter para ir" size="auto" maxWidth="max-w-xl" zIndex="z-[80]">
      <div className="space-y-3" onKeyDown={onKeyDown}>
        <Input
          variant="search"
          icon={loading ? 'progress_activity' : 'search'}
          placeholder="Escribe un taller, un email, un slug o una pantalla…"
          value={q}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)}
          autoFocus
          role="combobox"
          aria-expanded={items.length > 0}
          aria-controls="command-palette-results"
          aria-activedescendant={items[active] ? `cp-${items[active].key}` : undefined}
        />
        <ul id="command-palette-results" ref={listRef} role="listbox" className="max-h-[50vh] overflow-y-auto divide-y divide-border-subtle dark:divide-border-subtle-dark rounded-xl border border-border-subtle dark:border-border-subtle-dark">
          {items.length === 0 ? (
            <li className="px-4 py-6 text-center text-sm text-text-sub dark:text-slate-400">{loading ? 'Buscando…' : 'Sin resultados'}</li>
          ) : items.map((it, i) => (
            <li
              key={it.key}
              id={`cp-${it.key}`}
              data-index={i}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(it)}
              className={`px-4 py-2.5 flex items-center gap-3 cursor-pointer ${i === active ? 'bg-primary/10' : 'hover:bg-slate-50 dark:hover:bg-surface-hover-dark'}`}
            >
              <Icon name={it.icon} className="!text-[20px] text-slate-400 shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-text-main dark:text-white truncate">{it.title}</span>
                {it.subtitle ? <span className="block text-xs text-text-sub dark:text-slate-400 truncate">{it.subtitle}</span> : null}
              </span>
              {it.badge ? <StatusBadge status={it.badge.status} label={it.badge.label} /> : null}
            </li>
          ))}
        </ul>
      </div>
    </ModalShell>
  );
}
